import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';

// Components

import AnotherParentQuestionBlock from './pageIncludes/thcBlocks/AnotherParentQuestionBlock';
import ConfirmationBlock from './pageIncludes/thcBlocks/ConfirmationBlock';
import DependentsBlock from './pageIncludes/thcBlocks/DependentsBlock';
import FilersBlock from './pageIncludes/thcBlocks/FilersBlock';
import Form from '@lib/wrappers/Form';
import OutsideClaimsQuestionBlock from './pageIncludes/thcBlocks/OutsideClaimsQuestionBlock';
import PairsBlock from './pageIncludes/thcBlocks/PairsBlock';
import PrimaryFilerBlock from './pageIncludes/thcBlocks/PrimaryFilerBlock';
import TaxHouseholdCompositionSummary from './pageIncludes/summary/TaxHouseholdCompositionSummary';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators / Selectors

import { changeData, clearMethods, fetchAllBloodRelationships, setMethods } from '@actions/ssap/ssapActionCreators';
import { showModal, hideModal } from '@actions/common/actionCreators';
import { selectIfFlipLogicForLivesWithParentEnabled } from '@reducers/appReducer';

// Utils

import { analyzeTaxHouseholdComposition, checkIfMultiTaxHousehold } from '@utils/householdMember/analyzeTaxHouseholdComposition';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { getOutsideClaimsInputsStatuses } from '@utils/ssap/page-utils';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { preventerTools } from '@utils/tools/preventerTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';

// Constants

import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { THC_TYPES } from '@constants/ssap/hhmTypes/taxRelationsTypes';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';



const Page2_07_E2_mod1 = props => {

    const {
        data,
        data: {
            coverageYear,
            taxHousehold: {
                anyChangesInTaxhouseholdComposition,
                thCommonParams: {
                    hasExternalFilers,
                    hasNonCustodialParentClaimers,
                    hasOtherThanSpouseOrParentClaimers,
                    hasTaxFilers
                }
            }
        },
        environment: {
            localDates: {
                serverDate
            },
            isState
        },
        form,
        householdMembers,
        relations,
        relations: {
            codes,
            loadedRelationsWholeList = []
        },
        taxHousehold: {
            bloodRelationship,
            primaryTaxFilerPersonId
        }
    } = props;

    const {
        buttonContainer: ButtonContainer,
        formName,
        itfFeatureEnabled
    } = props;

    const {
        isEligiblyForSave,
        isFormSubmitted,
        isFormOnTopLevelValid,
        registeredInputs
    } = preventerTools.provideObject(form[formName]);

    const flipLogicForLivesWithParentEnabled = useSelector(selectIfFlipLogicForLivesWithParentEnabled);

    const [ isFirstRender, toggleFirstRender ] = useState(true);

    const prepareWarnings = () => householdMembers.map((hhm, idx) => ({
        idx,
        isWarningShown: false,
        itfFullName: null
    }));

    const [ itfWarnings, setItfWarning ] = useState(prepareWarnings());

    const family = useMemo(
        () => {
            return analyzeTaxHouseholdComposition({
                householdMembers,
                relations,
                bloodRelationship,
                serverDate,
                primaryTaxFilerPersonId,
                isState
            });
        }, [
            householdMembers,
            relations,
            bloodRelationship,
            serverDate,
            primaryTaxFilerPersonId,
            isState
        ]
    );

    useEffect(() => {
        loadedRelationsWholeList.length === 0 && props.actions.fetchAllBloodRelationships();
    }, []);

    useEffect(() => {
        props.actions.setMethods('Page2_07_E2_mod1', { handleSaveClick });
        return () => {
            props.actions.clearMethods('Page2_07_E2_mod1');
        };
    }, [
        family.filers.map(f => f.filerHouseholdId).toString(),
        family.filers.map(f => f.isFilingTaxes).toString(),
        family.dependents.map(d => d.isDependent).toString(),
        family.independentTaxFilers.toString(),
        primaryTaxFilerPersonId,
        isEligiblyForSave
    ]);

    useEffect(() => {

        if(!isFirstRender) {

            const newState = { ...data };

            if(family.filersQty === 1) {
                newState
                    .taxHousehold
                    .primaryTaxFilerPersonId = family.filers.find(filer => filer.isFilingTaxes).filerPersonId;
            } else {
                newState
                    .taxHousehold
                    .primaryTaxFilerPersonId = 0;
            }

            newState
                .taxHousehold
                .householdMember = householdMembers.map(hhm => {
                    const {
                        applicantGuid,
                        taxHouseholdComposition: {
                            taxRelationship,
                            taxFilingStatus
                        }
                    } = hhm;

                    switch(taxRelationship) {
                        case THC_TYPES.FILER:
                            return analyzeTaxHouseholdComposition.updateHhm(hhm, {
                                taxFilingStatus: householdMemberTools.checkIfHhmMarriedByGuid(bloodRelationship, applicantGuid, codes) ? taxFilingStatus : THC_TYPES.SINGLE,
                                claimerIds: []
                            });
                        case THC_TYPES.FILER_DEPENDENT:
                            return analyzeTaxHouseholdComposition.updateHhm(hhm, {
                                taxFilingStatus: householdMemberTools.checkIfHhmMarriedByGuid(bloodRelationship, applicantGuid, codes) ? taxFilingStatus : THC_TYPES.SINGLE,
                                taxRelationship: THC_TYPES.FILER,
                                claimerIds: []
                            });
                        default:
                            return analyzeTaxHouseholdComposition.updateHhm(hhm, {
                                taxFilingStatus: THC_TYPES.UNSPECIFIED,
                                taxRelationship: THC_TYPES.UNSPECIFIED,
                                claimerIds: []
                            });
                    }
                });

            props.actions.changeData(newState);
        }
    }, [ family.filersQty ]);

    const watchedValues = [
        family.filteredCombinedFilers
            .reduce((res, filer) => { return res + (Array.isArray(filer.filerIndex) ? 2 : 1); }, 0),

        family.pairs.map(pair => pair.isPairMadeChoice && pair.doSpousesFileTogether).toLocaleString(),

        family.dependents
            .filter(d => d.isDependent).length
    ];

    useEffect(() => {

        const newState = { ...data };

        if(family.filteredCombinedFilers.length !== 0) {

            // In case of any changes in filers' structure we set householdId to 0 before recalculating
            // Back-end handles only 0 as default. In case of any change warn back-end developer!

            newState
                .taxHousehold
                .householdMember = newState
                    .taxHousehold
                    .householdMember.map(hhm => ({
                        ...hhm,
                        householdId: 0
                    }));

            let counter = -1;

            family.filteredCombinedFilers.forEach(filer => {
                if(Array.isArray(filer.filerIndex) && filer.taxFilingStatus === THC_TYPES.FILING_JOINTLY ) {

                    counter++;

                    filer.filerIndex.forEach(idx => {
                        newState
                            .taxHousehold
                            .householdMember[idx]
                            .householdId = counter;
                    });
                } else {
                    newState
                        .taxHousehold
                        .householdMember[filer.filerIndex]
                        .householdId = ++counter;
                }
            });
        }

        family.dependents
            .filter(d => d.isDependent)
            .forEach(d => {
                const  {
                    claimerIds,
                    dependentIndex
                } = d;

                // Read from newState so dependents follow renumbered filer householdIds
                newState
                    .taxHousehold
                    .householdMember[dependentIndex]
                    .householdId = newState
                        .taxHousehold
                        .householdMember[claimerIds[0] - 1]
                        .householdId;
            });

        props.actions.changeData(newState);

    }, [ ...watchedValues ]);

    useEffect(() => {
        const newState = { ...data };

        newState
            .taxHousehold
            .householdMember = newState
                .taxHousehold
                .householdMember.map((hhm, idx) => {
                    return {
                        ...hhm,
                        taxHouseholdComposition: {
                            ...hhm.taxHouseholdComposition,
                            independentTaxFiler: family.independentTaxFilers.includes(idx)
                        }
                    };
                });
        props.actions.changeData(newState);
    }, [
        family.independentTaxFilers.toString()
    ]);

    const outsideClaimsInputStatuses = useMemo(() => {
        return getOutsideClaimsInputsStatuses(registeredInputs);
    }, [ registeredInputs ]);

    /**
     * Creates an updateIndividualValues function for outside claims questions.
     * This helper reduces code duplication across the three question handlers.
     * The aove 2 lines to be removed after discussion with Yury
     * @param {Set<number>|undefined} indexesOfHhms - Set of household member indexes for the question
     * @param {Object} baseUpdates - Updates to apply to all household members
     * @param {Object} conditionalUpdates - Updates to apply only when idx is in indexesOfHhms
     * @returns {Function} updateIndividualValues function that takes (hhm, idx) and returns updated hhm
     */
    const createUpdateIndividualValues = (indexesOfHhms, baseUpdates = {}, conditionalUpdates = {}) => {
        return (hhm, idx) => {
            const shouldApplyConditionalUpdates = indexesOfHhms?.has(idx);
            return {
                ...hhm,
                taxHouseholdComposition: {
                    ...hhm.taxHouseholdComposition,
                    ...baseUpdates,
                    ...(shouldApplyConditionalUpdates ? conditionalUpdates : {})
                }
            };
        };
    };

    const onFieldChange = (...args) => {
        const { disabled, indexInArray, name, value } = parseFieldChangeArgs(args);

        if(disabled) {
            return null;
        }

        let newState = { ...data };

        const hhmIndex = Array.isArray(indexInArray) ? indexInArray[0] : indexInArray;

        const hhm = newState
            .taxHousehold
            .householdMember[hhmIndex];

        switch(name) {

            case 'isTaxFiler':

                newState
                    .taxHousehold
                    .householdMember[hhmIndex] = analyzeTaxHouseholdComposition.updateHhm(hhm, {
                        taxRelationship:  value ? THC_TYPES.FILER : THC_TYPES.UNSPECIFIED
                    });

                if(
                    householdMemberTools
                        .checkIfHhmMarriedByGuid(
                            bloodRelationship,
                            householdMemberTools.extractFromHouseholdMembers(householdMembers).getGuidByPersonId(hhmIndex + 1),
                            codes
                        )
                ) {

                    const spouseIndex = analyzeTaxHouseholdComposition.getSpouseIndex(hhmIndex);

                    newState
                        .taxHousehold
                        .householdMember[spouseIndex] = analyzeTaxHouseholdComposition.updateHhm(analyzeTaxHouseholdComposition.getHhmByPersonId(spouseIndex + 1, householdMembers), {
                            taxFilingStatus:  THC_TYPES.UNSPECIFIED
                        });
                }

                if(newState
                    .taxHousehold
                    .householdMember.some(hhm => {
                        const {
                            taxHouseholdComposition: {
                                taxRelationship
                            }
                        } = hhm;
                        return (taxRelationship === THC_TYPES.FILER || taxRelationship === THC_TYPES.FILER_DEPENDENT);
                    })
                ) {
                    newState = dataSyncingTools.syncThCommonProps(
                        newState,
                        {
                            propName: 'taxFiler',
                            trueValue: THC_TYPES.FILER
                        }
                    );
                }

                toggleFirstRender(false);
                break;

            case 'hasTaxFilers': {

                newState = {
                    ...newState,
                    taxHousehold: {
                        ...newState.taxHousehold,
                        thCommonParams: {
                            ...newState.taxHousehold.thCommonParams,
                            hasTaxFilers: value
                        }
                    }
                };

                if(value === false) {
                    newState = {
                        ...newState,
                        taxHousehold: {
                            ...newState.taxHousehold,
                            primaryTaxFilerPersonId: 0
                        },
                    };
                }

                const updateIndividualValues = hhm => {
                    return {
                        ...hhm,
                        householdId: 0,
                        taxHouseholdComposition: {
                            ...hhm.taxHouseholdComposition,
                            ...EMPTY_OBJECTS.HOUSEHOLD_COMPOSITION
                        }
                    };
                };

                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        customUpdater: updateIndividualValues,
                        isCommon: true,
                        propName: 'taxFiler'
                    }
                );
            }
                break;

            case 'spousesChoice':

                (() => {
                    if(value === 'jointly') {
                        newState
                            .taxHousehold
                            .primaryTaxFilerPersonId = 0;
                    }

                    newState
                        .taxHousehold
                        .householdMember = newState
                            .taxHousehold
                            .householdMember.map(hhm => {
                                const {
                                    taxHouseholdComposition: {
                                        taxRelationship
                                    }
                                } = hhm;

                                const getNewTaxRelationship = taxRelationship => {
                                    switch(taxRelationship) {
                                        case THC_TYPES.FILER:
                                        case THC_TYPES.FILER_DEPENDENT:
                                            return THC_TYPES.FILER;
                                        case THC_TYPES.DEPENDENT:
                                            return THC_TYPES.UNSPECIFIED;
                                        default:
                                            return THC_TYPES.UNSPECIFIED;
                                    }
                                };

                                return {
                                    ...hhm,
                                    taxHouseholdComposition: {
                                        ...hhm.taxHouseholdComposition,
                                        taxRelationship: getNewTaxRelationship(taxRelationship),
                                        claimerIds: []
                                    }
                                };
                            });

                    if(Array.isArray(indexInArray)) {

                        indexInArray.forEach(spouseIndex => {

                            const {
                                taxRelationship: spouseTaxRelationship
                            } = analyzeTaxHouseholdComposition
                                .getHhmByPersonId(spouseIndex + 1, householdMembers)
                                .taxHouseholdComposition;

                            newState
                                .taxHousehold
                                .householdMember[spouseIndex] = analyzeTaxHouseholdComposition.updateHhm(analyzeTaxHouseholdComposition.getHhmByPersonId(spouseIndex + 1, householdMembers), {
                                    taxRelationship:  value === 'jointly' ? THC_TYPES.FILER : spouseTaxRelationship
                                });
                        });

                    }

                    const updatedHhm = index => newState
                        .taxHousehold
                        .householdMember[index];

                    indexInArray.forEach(index => {
                        newState
                            .taxHousehold
                            .householdMember[index] = analyzeTaxHouseholdComposition.updateHhm(updatedHhm(index),{
                                taxFilingStatus:  value === 'jointly' ?
                                    THC_TYPES.FILING_JOINTLY :
                                    (updatedHhm(index).taxHouseholdComposition.taxRelationship === THC_TYPES.FILER ||
                                updatedHhm(index).taxHouseholdComposition.taxRelationship === THC_TYPES.FILER_DEPENDENT) ?
                                        THC_TYPES.FILING_SEPARATELY : THC_TYPES.UNSPECIFIED,
                            });
                    });
                })();
                toggleFirstRender(false);
                break;

            case 'isPrimaryTaxFiler':

                if(itfFeatureEnabled) {
                    newState
                        .taxHousehold = {
                            ...newState.taxHousehold,
                            primaryTaxFilerPersonId: hhmIndex + 1,
                            householdMember: newState.taxHousehold.householdMember.map(hhm => {
                                return {
                                    ...hhm,
                                    taxHouseholdComposition: {
                                        ...hhm.taxHouseholdComposition,
                                        claimerIds: [],
                                        taxFilingStatus: hhm.taxHouseholdComposition.taxFilingStatus === THC_TYPES.HEAD_OF_HOUSEHOLD
                                            ? THC_TYPES.SINGLE
                                            : hhm.taxHouseholdComposition.taxFilingStatus,
                                        taxRelationship: hhm.taxHouseholdComposition.taxRelationship === THC_TYPES.FILER_DEPENDENT
                                            ? THC_TYPES.FILER
                                            : hhm.taxHouseholdComposition.taxRelationship === THC_TYPES.DEPENDENT
                                                ? THC_TYPES.UNSPECIFIED
                                                : hhm.taxHouseholdComposition.taxRelationship
                                    }
                                };
                            })
                        };
                } else {
                    newState
                        .taxHousehold
                        .primaryTaxFilerPersonId = hhmIndex + 1;
                }
                break;

            case 'isDependent': {

                const [dependentIndex, filerPersonId] = indexInArray;

                let hhmProcessedWithITF = hhm;

                if(itfFeatureEnabled) {

                    let filersForDependent = filerPersonId;

                    if(!Array.isArray(filerPersonId)) {
                        filersForDependent = [ filerPersonId ];
                    }

                    const isITFDependent = filersForDependent.some(filerPersonId => {
                        return analyzeTaxHouseholdComposition
                            .getHhmByPersonId(filerPersonId, householdMembers)
                            .taxHouseholdComposition
                            .independentTaxFiler;
                    });

                    if(isITFDependent) {
                        if(value) {
                            hhmProcessedWithITF = analyzeTaxHouseholdComposition
                                .updateHhmTopProp(hhm, { applyingForCoverageIndicator: false });
                        }

                        let itfFullName;

                        if(!Array.isArray(filerPersonId)) {
                            itfFullName = normalizeNameOfPerson(
                                analyzeTaxHouseholdComposition
                                    .getHhmByPersonId(filerPersonId, householdMembers)
                                    .name
                            );
                        } else {
                            itfFullName = filersForDependent
                                .map(filerPersonId => normalizeNameOfPerson(
                                    analyzeTaxHouseholdComposition
                                        .getHhmByPersonId(filerPersonId, householdMembers)
                                        .name
                                ));
                        }

                        setItfWarning(warnings =>
                            warnings.reduce((warnings, warning) => {
                                const { idx } = warning;
                                return idx === dependentIndex
                                    ? [
                                        ...warnings,
                                        {
                                            ...warning,
                                            ...(!value ? { isWarningShown: true } : {}),
                                            itfFullName
                                        }
                                    ]
                                    : [
                                        ...warnings,
                                        warning
                                    ];
                            }, []));
                    }
                }

                newState
                    .taxHousehold
                    .householdMember[hhmIndex] = analyzeTaxHouseholdComposition.updateHhm(hhmProcessedWithITF, {
                        taxRelationship:
                        value ?
                            (r => r === THC_TYPES.FILER ? THC_TYPES.FILER_DEPENDENT :  THC_TYPES.DEPENDENT)(analyzeTaxHouseholdComposition.getIndicatorsValues(hhmIndex + 1).taxRelationship) :
                            (r => r === THC_TYPES.FILER_DEPENDENT ? THC_TYPES.FILER :  THC_TYPES.UNSPECIFIED)(analyzeTaxHouseholdComposition.getIndicatorsValues(hhmIndex + 1).taxRelationship)
                    });

                newState
                    .taxHousehold
                    .householdMember[indexInArray[0]]
                    .taxHouseholdComposition
                    .claimerIds = value ? (Array.isArray(indexInArray[1]) ? indexInArray[1] : [indexInArray[1]]) : [];
                break;
            }

            case 'anyChangesInTaxhouseholdComposition':

                newState.taxHousehold.anyChangesInTaxhouseholdComposition = value;

                if(value) {
                    newState = {
                        ...newState,
                        taxHousehold: {
                            ...newState.taxHousehold,
                            primaryTaxFilerPersonId: 0,
                            thCommonParams: {
                                ...newState.taxHousehold.thCommonParams,
                                hasTaxFilers: null,
                                hasExternalFilers: null,
                                hasNonCustodialParentClaimers: null
                            },
                            householdMember: newState
                                .taxHousehold
                                .householdMember.map(hhm => ({
                                    ...hhm,
                                    householdId: 0,

                                    taxHouseholdComposition: {
                                        ...hhm.taxHouseholdComposition,
                                        ...EMPTY_OBJECTS.HOUSEHOLD_COMPOSITION
                                    }
                                }))
                        }
                    };
                }
                break;

            case 'livesSeparatelyFromClaimerParent':

                newState
                    .taxHousehold
                    .householdMember[hhmIndex]
                    .taxHouseholdComposition = {
                        ...newState.taxHousehold.householdMember[hhmIndex].taxHouseholdComposition,
                        // flipLogicForLivesWithParent Enabled (MN by default): direct mapping (true -> Yes, false -> No)
                        // Disabled (other states): inverted mapping (true -> No, false -> Yes)
                        livesSeparatelyFromClaimerParent: flipLogicForLivesWithParentEnabled ? value : !value
                    };
                break;

            case 'isDependentOnExternalFiler':

                newState
                    .taxHousehold
                    .householdMember[hhmIndex]
                    .taxHouseholdComposition = {
                        ...newState.taxHousehold.householdMember[hhmIndex].taxHouseholdComposition,
                        dependentOnExternalFiler: value,
                        ...(!value && {
                            claimedByNonCustodialParent : null,
                            claimedByOtherThanSpouseOrParent : null
                        })
                    };

                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        propName: 'externalTaxFiler'
                    }
                );

                break;

            case 'isClaimedByNonCustodialParent':

                newState
                    .taxHousehold
                    .householdMember[hhmIndex]
                    .taxHouseholdComposition = {
                        ...newState.taxHousehold.householdMember[hhmIndex].taxHouseholdComposition,
                        claimedByNonCustodialParent: value,
                        ...(value && {
                            claimedByOtherThanSpouseOrParent : null
                        })
                    };

                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        propName: 'nonCustodialParentClaimer'
                    }
                );

                break;

            case 'isClaimedByOtherThanSpouseOrParent':

                newState
                    .taxHousehold
                    .householdMember[hhmIndex]
                    .taxHouseholdComposition = {
                        ...newState.taxHousehold.householdMember[hhmIndex].taxHouseholdComposition,
                        claimedByOtherThanSpouseOrParent: value
                    };

                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        propName: 'otherThanSpouseOrParentClaimer'
                    }
                );

                break;

            case 'hasExternalFilers': {

                newState = {
                    ...newState,
                    taxHousehold: {
                        ...newState.taxHousehold,
                        thCommonParams: {
                            ...newState.taxHousehold.thCommonParams,
                            hasExternalFilers: value,
                            ...(value === false && {
                                hasNonCustodialParentClaimers: null ,
                                hasOtherThanSpouseOrParentClaimers: null
                            })
                        }
                    }
                };

                const updateIndividualValues = createUpdateIndividualValues(
                    outsideClaimsInputStatuses?.firstQuestionInputs?.indexesOfHhms,
                    {
                        claimedByNonCustodialParent: null,
                        claimedByOtherThanSpouseOrParent: null
                    },
                    {
                        dependentOnExternalFiler: false
                    }
                );

                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        customUpdater: updateIndividualValues,
                        isCommon: true,
                        propName: 'externalTaxFiler'
                    }
                );
            }
                break;

            case 'hasNonCustodialParentClaimers': {

                newState = {
                    ...newState,
                    taxHousehold: {
                        ...newState.taxHousehold,
                        thCommonParams: {
                            ...newState.taxHousehold.thCommonParams,
                            hasNonCustodialParentClaimers: value,
                            hasOtherThanSpouseOrParentClaimers: null
                        }
                    }
                };

                const updateIndividualValues = createUpdateIndividualValues(
                    outsideClaimsInputStatuses?.secondQuestionInputs?.indexesOfHhms,
                    {},
                    {
                        claimedByNonCustodialParent: false,
                        claimedByOtherThanSpouseOrParent: null
                    }
                );

                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        customUpdater: updateIndividualValues,
                        isCommon: true,
                        propName: 'nonCustodialParentClaimer'
                    }
                );
            }
                break;

            case 'hasOtherThanSpouseOrParentClaimers': {
                newState = {
                    ...newState,
                    taxHousehold: {
                        ...newState.taxHousehold,
                        thCommonParams: {
                            ...newState.taxHousehold.thCommonParams,
                            hasOtherThanSpouseOrParentClaimers: value
                        }
                    }
                };

                const updateIndividualValues = createUpdateIndividualValues(
                    outsideClaimsInputStatuses?.thirdQuestionInputs?.indexesOfHhms,
                    {},
                    {
                        claimedByOtherThanSpouseOrParent: false
                    }
                );

                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        customUpdater: updateIndividualValues,
                        isCommon: true,
                        propName: 'otherThanSpouseOrParentClaimer'
                    }
                );
            }
                break;
        }

        newState
            .taxHousehold
            .householdMember = newState
                .taxHousehold
                .householdMember.map(hhm => {
                    const {
                        personId,
                        taxHouseholdComposition: {
                            claimerIds,
                            taxFilingStatus
                        }
                    } = hhm;

                    const exemptions = [
                        THC_TYPES.FILING_JOINTLY,
                        THC_TYPES.FILING_SEPARATELY,
                        THC_TYPES.UNSPECIFIED
                    ];

                    if(!exemptions.some(e => e === taxFilingStatus)) {
                        return analyzeTaxHouseholdComposition.updateHhm(hhm, {
                            taxFilingStatus: analyzeTaxHouseholdComposition.hasDependents(personId, claimerIds) ?
                                THC_TYPES.HEAD_OF_HOUSEHOLD : THC_TYPES.SINGLE
                        });
                    } else {
                        return hhm;
                    }
                });

        const propsChangeRequireCleanup = [
            'isTaxFiler',
            'spousesChoice',
            'isDependent'
        ];

        const propsToSyncUpAndCleanUp = [
            'externalTaxFiler',
            'nonCustodialParentClaimer',
            'otherThanSpouseOrParentClaimer'
        ];

        if(propsChangeRequireCleanup.includes(name)) {
            propsToSyncUpAndCleanUp.forEach(prop => {
                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        propName: prop
                    }
                );

                newState = dataSyncingTools.syncThCommonProps(
                    newState,
                    {
                        isCommon: true,
                        propName: prop
                    }
                );
            });
        }

        props.actions.changeData(newState);
    };

    const listOfClaimedBySeparatelyLivingParent = family.dependents
        .filter(d => d.isDependent && d.isChildClaimedBySeparatelyLivingParent);

    const isFilerQuestionResolved = anyChangesInTaxhouseholdComposition === false
        && (hasTaxFilers === false || family.filers.some(f => f.isFilingTaxes));

    const checkTopLevelValidation = () => {

        const areOutsideClaimersQuestionsResolved = (statuses => {
            return Object.values(statuses).every(inputGroup => inputGroup.isAnswerProvided === true);
        })(outsideClaimsInputStatuses);

        const conditions = [
            anyChangesInTaxhouseholdComposition === false,
            isFilerQuestionResolved,
            family.filersQty > 1 ? primaryTaxFilerPersonId !== 0 : true,
            areOutsideClaimersQuestionsResolved
        ];

        return conditions.every(c => c === true);
    };

    const handleSaveClick = (...args) => {

        const [ callback, householdMembers ] = args;

        const {
            isMultiTaxHousehold,
            filteredFilers
        } = checkIfMultiTaxHousehold({
            combinedFilers: family.combinedFilers,
            filers: family.filers,
            isState,
            primaryTaxFilerPersonId,
            bloodRelationship,
        });

        const independentTaxFilers = (() => {
            return householdMembers.reduce((itfs, hhm) => {
                const { name, taxHouseholdComposition: { independentTaxFiler } } = hhm;
                return independentTaxFiler
                    ? [ ...itfs, normalizeNameOfPerson(name) ]
                    : itfs;
            }, []);
        })();

        if(isEligiblyForSave) {
            if(itfFeatureEnabled && independentTaxFilers.length) {
                props.actions.showModal({
                    modalType: MODAL_TYPES.INDEPENDENT_TAX_FILERS_MODAL,
                    modalData: {
                        independentTaxFilers,
                        callbacks: [ callback ]
                    }
                });
            } else if(isMultiTaxHousehold){
                props.actions.showModal({
                    modalType: MODAL_TYPES.MULTI_TAX_HOUSEHOLD_MODAL,
                    modalData: {
                        callbacks: [ callback ],
                        filers: family.filers,
                        filteredFilers,
                        primaryTaxFilerPersonId,
                    }
                });
            } else {
                callback();
            }
        } else {
            callback();
        }
    };

    return (
        <Fragment>
            <Form
                topLevelValidity = { checkTopLevelValidation() }
                { ...{
                    formName
                } }
            >
                <TaxHouseholdCompositionSummary
                    { ...{
                        family,
                        isState
                    } }
                />

                <ValidationErrorMessage
                    isVisible = { isFormSubmitted && !isFormOnTopLevelValid && anyChangesInTaxhouseholdComposition }
                    messageText = { VALIDATION_ERROR_MESSAGES.UPDATE_INFORMATION }
                    errorClasses = { [ 'margin-l-0', 'margin-b-10' ] }
                />

                <ConfirmationBlock
                    { ...{
                        anyChangesInTaxhouseholdComposition,
                        ButtonContainer,
                        onFieldChange,
                        underFormControl: true
                    } }
                />

                {
                    anyChangesInTaxhouseholdComposition === false &&

                        <Fragment>

                            <ValidationErrorMessage
                                isVisible = { isFormSubmitted && !isFormOnTopLevelValid && !isFilerQuestionResolved }
                                messageText = { VALIDATION_ERROR_MESSAGES.DEFINE_TAX_FILERS }
                                errorClasses = { [ 'margin-l-0', 'margin-b-10' ] }
                            />

                            <FilersBlock
                                { ...{
                                    analyzeFamily: analyzeTaxHouseholdComposition,
                                    coverageYear,
                                    hasTaxFilers,
                                    householdMembers,
                                    onFieldChange,
                                    underFormControl: true
                                } }
                            />

                            <PairsBlock
                                { ...{
                                    coverageYear,
                                    family,
                                    onFieldChange,
                                    underFormControl: true
                                } }
                            />

                            <PrimaryFilerBlock
                                { ...{
                                    family,
                                    householdMembers,
                                    isFormSubmitted,
                                    isFormOnTopLevelValid,
                                    onFieldChange,
                                    primaryTaxFilerPersonId,
                                    underFormControl: true
                                } }
                            />

                            <DependentsBlock
                                { ...{
                                    analyzeFamily: analyzeTaxHouseholdComposition,
                                    family,
                                    itfWarnings,
                                    onFieldChange,
                                    underFormControl: true
                                } }
                            />

                            <AnotherParentQuestionBlock
                                { ...{
                                    listOfClaimedBySeparatelyLivingParent,
                                    householdMembers,
                                    onFieldChange,
                                    underFormControl: true
                                } }
                            />

                            <OutsideClaimsQuestionBlock
                                { ...{
                                    family,
                                    hasExternalFilers,
                                    hasNonCustodialParentClaimers,
                                    hasOtherThanSpouseOrParentClaimers,
                                    householdMembers,
                                    isFormSubmitted,
                                    isFormOnTopLevelValid,
                                    isState,
                                    onFieldChange,
                                    outsideClaimsInputStatuses,
                                    underFormControl: true,
                                } }
                            />
                        </Fragment>
                }
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    data: state.ssap.data,
    form: state.ssap.form,

    relations: state
        .ssap
        .relations,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    taxHousehold: state
        .ssap
        .data
        .taxHousehold
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        clearMethods,
        fetchAllBloodRelationships,
        hideModal,
        setMethods,
        showModal
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_07_E2_mod1);
